import { computed, ComputedRef, Ref, ref } from "vue";
import { datadogRum } from "@datadog/browser-rum";
import MerchantFlags from "@/apps/merchant3/types/MerchantFlags";
import AvailableFeatureFlags from "@/apps/merchant3/types/AvailableFeatureFlags";
import useFeatureFlag from "./useFeatureFlag";
import { useMerchantFlags } from "./useMerchantFlags";
import { lookupPersonByPhoneNumber } from "@/apps/merchant3/services/lookup-persons.service";

type ComposableResult = {
  isKycReuseEnabled: ComputedRef<boolean>;
  findPersonsByPhone: (
    cartTag: string,
    phoneNumber: string,
    email: string
  ) => Promise<boolean>;
  phoneMatchesWithPerson: Ref<boolean>;
  isFindPersonByPhoneInProgress: Ref<boolean>;
};

export function useKycReuse(): ComposableResult {
  const { isFeatureFlagSet } = useFeatureFlag();
  const { merchantHasFlag } = useMerchantFlags();

  const isKycReuseEnabled = computed(() => {
    const merchantHasKycReuseFlag = merchantHasFlag(
      MerchantFlags.REUSE_KYC_FLOW
    );

    return (
      merchantHasKycReuseFlag &&
      isFeatureFlagSet(
        AvailableFeatureFlags.REUSE_KYC_FLOW,
        merchantHasKycReuseFlag
      )
    );
  });

  const phoneMatchesWithPerson = ref(false);
  const isFindPersonByPhoneInProgress = ref(false);
  const findPersonsByPhone = async (
    cartTag: string,
    phoneNumber: string,
    email: string
  ): Promise<boolean> => {
    const findPersonsByPhoneStart = performance.now();
    isFindPersonByPhoneInProgress.value = true;

    try {
      const response = await lookupPersonByPhoneNumber(
        cartTag,
        phoneNumber,
        email
      );
      phoneMatchesWithPerson.value = response.match;
      return response.match;
    } finally {
      isFindPersonByPhoneInProgress.value = false;
      // Keep track of how long it took to lookup person by phone in RUM.
      const findPersonsByPhoneDuration =
        performance.now() - findPersonsByPhoneStart;
      datadogRum.addAction("m3-api-lookup-person-by-phone-finished", {
        loadTime: findPersonsByPhoneDuration,
      });
    }

    phoneMatchesWithPerson.value = false;
    return false;
  };

  return {
    isKycReuseEnabled,
    findPersonsByPhone,
    isFindPersonByPhoneInProgress,
    phoneMatchesWithPerson,
  };
}
